import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Utensils, Eye, EyeOff, Check, X 
} from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

// Password requirements matching Cognito policy
const PASSWORD_REQUIREMENTS = [
  {
    id: 'length',
    label: 'At least 12 characters',
    test: (p: string) => p.length >= 12 
  },
  {
    id: 'lowercase',
    label: 'One lowercase letter',
    test: (p: string) => /[a-z]/.test(p) 
  },
  {
    id: 'uppercase',
    label: 'One uppercase letter',
    test: (p: string) => /[A-Z]/.test(p) 
  },
  {
    id: 'number',
    label: 'One number',
    test: (p: string) => /\d/.test(p) 
  },
  {
    id: 'symbol',
    label: 'One special character',
    test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) 
  },
];

export function SetPasswordPage() {
  const {
    setNewPassword, pendingUsername, isLoading 
  } = useAuth();
  const navigate = useNavigate();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check all password requirements
  const requirementsMet = PASSWORD_REQUIREMENTS.map(req => ({
    ...req,
    met: req.test(password),
  }));
  
  const allRequirementsMet = requirementsMet.every(r => r.met);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const canSubmit = allRequirementsMet && passwordsMatch && !isSubmitting;

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!canSubmit) {
      return;
    }
    
    setIsSubmitting(true);
    void (async () => {
      try {
        await setNewPassword(password);
        navigate('/');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to set password');
      } finally {
        setIsSubmitting(false);
      }
    })();
  };

  // Redirect if not in new password required state
  if (!pendingUsername && !isLoading) {
    navigate('/login');
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary">
            <Utensils className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">Set Your Password</CardTitle>
          <CardDescription>
            Welcome to GalleyOS! Please create a new password to secure your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            
            {pendingUsername && (
              <div className="rounded-md bg-muted p-3 text-sm">
                <span className="text-muted-foreground">Signing in as: </span>
                <span className="font-medium">{pendingUsername}</span>
              </div>
            )}
            
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium leading-none">
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your new password"
                  autoComplete="new-password"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Password requirements checklist */}
            <div className="space-y-2 rounded-md bg-muted/50 p-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Password Requirements
              </p>
              <ul className="space-y-1">
                {requirementsMet.map(req => (
                  <li key={req.id} className="flex items-center gap-2 text-sm">
                    {req.met ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>
                      {req.label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium leading-none">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  autoComplete="new-password"
                  className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    confirmPassword && !passwordsMatch ? 'border-destructive' : 'border-input'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-xs text-destructive">Passwords do not match</p>
              )}
            </div>
            
            <Button 
              type="submit" 
              disabled={!canSubmit || isLoading} 
              className="w-full"
            >
              {isSubmitting ? 'Setting password...' : 'Set Password & Continue'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
